import { Skeleton, Space, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useAppFeature } from "src/core/hooks/useAppFeature";
import { useAuth } from "src/core/hooks/useAuth";
import { AppFeatureEnum } from "src/enums";
import { AdminPage } from "src/modules/AdminPage/AdminPage";
import { DataTableControlPanel } from "src/modules/DataTableControlPanel/DataTableControlPanel";
import { useDataTableControlFilter } from "src/modules/DataTableControlPanel/hooks/useDataTableControlFilter";
import { AddButton } from "src/modules/GlobalManualAdjustmentsManager/components/AddButton";
import { CostLevelEnum } from "src/modules/GlobalManualAdjustmentsManager/enums/enums";
import { useDataManageMethods } from "src/modules/GlobalManualAdjustmentsManager/hooks/useDataManageMethods";
import { useGlobalManualAdjustmentsColumns } from "src/modules/GlobalManualAdjustmentsManager/hooks/useGlobalManualAdjustmentsColumns";
import { GlobalManualAdjustmentItem } from "src/modules/GlobalManualAdjustmentsManager/types/types";
import { appParamsState, currentUserState } from "src/recoil/atoms";

export const GlobalManualAdjustmentsManager: React.FC = () => {
  useAppFeature(AppFeatureEnum.GLOBALMANUALADJUSTMENT);

  const [tableLoading, setTableLoading] = useState(false);

  const [dataItems, setDataItems] = useState<GlobalManualAdjustmentItem[]>();

  const appParams = useRecoilValue(appParamsState);
  const currentUser = useRecoilValue(currentUserState);

  const { authLoaded, success } = useAuth();

  const { t } = useTranslation();

  const secondaryFilter = useCallback(
    (filterString: string, item: GlobalManualAdjustmentItem) => {
      if (item.costLevel !== undefined) {
        return CostLevelEnum[item.costLevel]
          .toLowerCase()
          .includes(filterString.toLowerCase());
      }

      return false;
    },
    [],
  );

  const {
    debouncedOnFilterChange,
    filteredList,
    setFilteredList,
    onSecondaryFilterPropertyChange,
    clearFilter,
    filterValue,
    secondaryFilterValue,
    setSecondaryFilterValue,
  } = useDataTableControlFilter<GlobalManualAdjustmentItem>(
    dataItems,
    [
      "model",
      "description",
      "costTypeMapping",
      "typeOfCost",
      "father",
      "component",
      "madeIn.label",
      "componentMaterial.label",
    ],
    secondaryFilter,
  );

  const {
    handleAddClick,
    loadRecords,
    onExportClick,
    saveRecord,
    deleteRecord,
    deactivateAlert,
    duplicateRecord,
  } = useDataManageMethods<GlobalManualAdjustmentItem>(
    filteredList,
    setDataItems,
    setFilteredList,
    setTableLoading,
    clearFilter,
  );

  const { tableColumns } = useGlobalManualAdjustmentsColumns(
    saveRecord,
    deleteRecord,
    deactivateAlert,
    duplicateRecord,
  );

  useEffect(() => {
    void (async () => {
      if (authLoaded && success) {
        await loadRecords();
      }
    })();
  }, [authLoaded, loadRecords, success]);

  const onAddRecordClick = useCallback(async () => {
    handleAddClick(currentUser?.currency?.currencyCode);
  }, [currentUser?.currency?.currencyCode, handleAddClick]);

  const tableStyle = useMemo(
    () => ({ width: appParams.contentWidth - 60 }),
    [appParams.contentWidth],
  );

  const getRowClassName = useCallback(
    (rowItem: GlobalManualAdjustmentItem) =>
      (rowItem.id ?? -1) < 0 ? "row-type-class-unsaved-record" : "",
    [],
  );

  return (
    <AdminPage
      activePage="global-manual-adjustments"
      pageTitle={t("pageTitle.globalManualAdjustmentsTitle")}
    >
      <Skeleton loading={false} active>
        <Space direction="vertical">
          <DataTableControlPanel
            filterValue={filterValue}
            checkBoxFilterValue={secondaryFilterValue}
            setCheckBoxFilterValue={setSecondaryFilterValue}
            onFilterChange={debouncedOnFilterChange}
            placeHolder={t("placeholder.search") as string}
            checkBoxFilter={{
              property: "alertId",
              label: t("label.newCostLevelsAvailable"),
              onChange: onSecondaryFilterPropertyChange,
            }}
            onDownloadClick={async () => {
              await onExportClick(filteredList);
            }}
          />
          <AddButton onClick={onAddRecordClick} />
          <Table
            columns={tableColumns}
            loading={tableLoading}
            dataSource={filteredList}
            pagination={false}
            rowKey={"id"}
            sticky={true}
            scroll={{ x: "max-content" }}
            style={tableStyle}
            rowClassName={getRowClassName}
            size={"small"}
            bordered
          />
        </Space>
      </Skeleton>
    </AdminPage>
  );
};
